import { useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Alert, AlertDescription } from "@/shared/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { RadioGroup, RadioGroupItem } from "@/shared/ui/radio-group";
import { Label } from "@/shared/ui/label";
import { CheckCircle, Download, FileSpreadsheet, Upload } from "lucide-react";
import { downloadTemplate } from "./templateCsv";
import type { ImportMode } from "./fields";

interface Property {
  id: string;
  name: string;
  address: string;
}

interface UploadStepProps {
  propertyId?: string;
  properties: Property[];
  importMode: ImportMode;
  setImportMode: (mode: ImportMode) => void;
  selectedProperty: string;
  setSelectedProperty: (id: string) => void;
  file: File | null;
  parsedDataLength: number;
  onFileSelected: (file: File) => void;
}

export function UploadStep({
  propertyId,
  properties,
  importMode,
  setImportMode,
  selectedProperty,
  setSelectedProperty,
  file,
  parsedDataLength,
  onFileSelected,
}: UploadStepProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === "text/csv") {
      onFileSelected(droppedFile);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && (selectedFile.type === "text/csv" || selectedFile.name.endsWith(".csv"))) {
      onFileSelected(selectedFile);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Upload CSV File
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-4 mb-6">
          {!propertyId && (
            <div>
              <h3 className="text-lg font-medium mb-3">Import Mode</h3>
              <RadioGroup
                value={importMode}
                onValueChange={(value) => setImportMode(value as ImportMode)}
                className="flex gap-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="single" id="single" />
                  <Label htmlFor="single" className="cursor-pointer">
                    Single Property
                    <span className="block text-sm text-muted-foreground">All assets go to one selected property</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="multi" id="multi" />
                  <Label htmlFor="multi" className="cursor-pointer">
                    Multiple Properties
                    <span className="block text-sm text-muted-foreground">Assets assigned via property_id in CSV</span>
                  </Label>
                </div>
              </RadioGroup>
            </div>
          )}

          {!propertyId && importMode === "single" && (
            <div className="space-y-2">
              <Label htmlFor="property-select">Select Property</Label>
              <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a property for all assets" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((property) => (
                    <SelectItem key={property.id} value={property.id}>
                      {property.name} - {property.address}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {propertyId && (
            <p className="text-sm text-muted-foreground">
              Importing into: <span className="font-medium text-foreground">
                {properties.find((p) => p.id === propertyId)?.name ?? propertyId}
              </span>
            </p>
          )}
        </div>

        <Button onClick={() => downloadTemplate(importMode, properties)} variant="outline" className="mb-4">
          <Download className="h-4 w-4 mr-2" />
          Download Template ({importMode === "single" ? "Single Property" : "Multi-Property"})
        </Button>

        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/25"
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-lg font-medium mb-2">
            {file ? file.name : "Drop your CSV file here"}
          </p>
          <p className="text-sm text-muted-foreground mb-4">
            or click to browse files
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            className="hidden"
          />
          <Button onClick={() => fileInputRef.current?.click()}>
            Select File
          </Button>
        </div>

        {file && (
          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              File loaded: {parsedDataLength} rows found
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
